import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { useToast } from './ToastContext';
import { useNavigate } from 'react-router-dom';
import { subscribeToProfileSwitch } from '../lib/auth/resetClientStateForProfileSwitch';
import { isOnAuthPage, loginPathFor } from '../lib/auth/returnPath';
import { useLanguage } from './LanguageContext';

// Decode JWT payload and return claims, or null on failure
const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
    try {
        const payload = token.split('.')[1];
        return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return null;
    }
};

// AB#1616 — Read a token from localStorage. We deliberately do NOT
// pre-emptively reject expired tokens here. The axios response interceptor
// (src/services/api.ts) catches the 401 from any authenticated call,
// silently swaps the expired access token for a fresh one via /auth/refresh,
// and retries the original request. That entire mechanism only works when
// the access token is still in localStorage at request time (so the
// Authorization header gets attached and the BE returns 401 instead of a
// generic anonymous response) AND the refresh token survives.
//
// Previously this helper:
//   1. Returned null when access was expired → AuthContext rendered
//      isAuthenticated=false → router guard bounced to /login on every
//      reload after ~10 min of idle, even though refresh was valid for 7 days.
//   2. Removed BOTH tokens on expiry → the interceptor had no refresh to
//      use, defeating the renewal mechanism entirely.
//
// Both behaviors are removed. Trust the interceptor to do its job.
const getValidStoredToken = (key: string): string | null => {
    return localStorage.getItem(key);
};

interface AuthContextType {
    accessToken: string | null;
    refreshToken: string | null;
    setTokens: (accessToken: string, refreshToken: string) => void;
    clearTokens: () => void;
    isAuthenticated: boolean;
    userType?: number | null;
    staffRole?: number | null;
    staffId?: number | null;
    businessId?: number | null;
    isSalonOwner: boolean;
    isSalonStaff: boolean;
    isSuperAdmin: boolean;
    isStaffManager: boolean;
    canManageStaff: boolean;
    canDeleteStaff: boolean;
    /**
     * AB#1612 Phase 1A.2 — `true` when the current session was created by
     * logging in with an email identifier whose `EmailVerified` flag is
     * still false. The login itself succeeded (soft gate); this flag drives
     * the `<EmailVerificationBanner />` displayed on every authenticated
     * page. Cleared by `markEmailVerified()` after the verify-email flow
     * succeeds, and by `clearTokens()` on logout.
     */
    emailVerificationRequired: boolean;
    setEmailVerificationRequired: (required: boolean) => void;
    /** Convenience: same as setEmailVerificationRequired(false). */
    markEmailVerified: () => void;
    /** Email captured at login time, surfaced in the banner. */
    pendingVerificationEmail: string | null;
    setPendingVerificationEmail: (email: string | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [accessToken, setAccessToken] = useState<string | null>(() => getValidStoredToken('accessToken'));
    const [refreshToken, setRefreshToken] = useState<string | null>(() => localStorage.getItem('refreshToken'));
    // AB#1612 Phase 1A.2 — persist email-verification gate across reloads
    // so the banner survives a hard refresh. Cleared on logout / successful
    // verify-email.
    const [emailVerificationRequired, setEmailVerificationRequiredState] = useState<boolean>(
        () => localStorage.getItem('emailVerificationRequired') === 'true',
    );
    const [pendingVerificationEmail, setPendingVerificationEmailState] = useState<string | null>(
        () => localStorage.getItem('pendingVerificationEmail'),
    );
    const { showToast } = useToast();
    const navigate = useNavigate();

    const setTokens = (newAccessToken: string, newRefreshToken: string) => {
        apiService.setTokens(newAccessToken, newRefreshToken);
        setAccessToken(newAccessToken);
        setRefreshToken(newRefreshToken);
    };

    const setEmailVerificationRequired = useCallback((required: boolean) => {
        setEmailVerificationRequiredState(required);
        if (required) {
            localStorage.setItem('emailVerificationRequired', 'true');
        } else {
            localStorage.removeItem('emailVerificationRequired');
        }
    }, []);

    const setPendingVerificationEmail = useCallback((email: string | null) => {
        setPendingVerificationEmailState(email);
        if (email) {
            localStorage.setItem('pendingVerificationEmail', email);
        } else {
            localStorage.removeItem('pendingVerificationEmail');
        }
    }, []);

    const markEmailVerified = useCallback(() => {
        setEmailVerificationRequired(false);
        setPendingVerificationEmail(null);
    }, [setEmailVerificationRequired, setPendingVerificationEmail]);

    const clearTokens = useCallback(() => {
        apiService.clearTokens();
        setAccessToken(null);
        setRefreshToken(null);
        // Clear email-verification banner state on logout — different user
        // session may have a different verification status.
        localStorage.removeItem('emailVerificationRequired');
        localStorage.removeItem('pendingVerificationEmail');
        setEmailVerificationRequiredState(false);
        setPendingVerificationEmailState(null);
        showToast('Your session has ended. Please login again.', 'info');
        navigate('/form/login');
    }, [showToast, navigate]);

    // AB#2201 — the session expired and could not be refreshed (dispatched by the API
    // interceptor). Same clean-up as a logout, but the owner is told why and lands on
    // login with `returnTo` = the page they were on, so signing in again takes them
    // straight back. Fires once per expiry even when several requests 401 together.
    const { t } = useLanguage();
    const expiringRef = React.useRef(false);
    const expireResetTimer = React.useRef<number | undefined>(undefined);
    const expireSession = useCallback(() => {
        // Already on login (late 401 from an in-flight request): just make sure the
        // session state is gone — keep the existing returnTo and don't toast again.
        if (isOnAuthPage()) {
            apiService.clearTokens();
            setAccessToken(null);
            setRefreshToken(null);
            return;
        }
        if (expiringRef.current) return;
        expiringRef.current = true;
        const here = `${window.location.pathname}${window.location.search}`;
        apiService.clearTokens();
        setAccessToken(null);
        setRefreshToken(null);
        localStorage.removeItem('emailVerificationRequired');
        localStorage.removeItem('pendingVerificationEmail');
        setEmailVerificationRequiredState(false);
        setPendingVerificationEmailState(null);
        showToast(t('session.expired'), 'info');
        navigate(loginPathFor(here), { replace: true });
        expireResetTimer.current = window.setTimeout(() => { expiringRef.current = false; }, 2000);
    }, [showToast, navigate, t]);
    useEffect(() => () => window.clearTimeout(expireResetTimer.current), []);

    // Listen for session expiry events dispatched by the API interceptor (e.g. after a
    // failed token refresh mid-session). This keeps api.ts decoupled from React state.
    useEffect(() => {
        window.addEventListener('auth:session-expired', expireSession);
        return () => window.removeEventListener('auth:session-expired', expireSession);
    }, [expireSession]);

    // AB#1437/AB#1434.b — Auth v2 profile switch: sync React state with the new
    // scoped access token written to localStorage by resetClientStateForProfileSwitch.
    // Without this, AuthContext.accessToken (and all derived flags: isSuperAdmin,
    // isSalonOwner, userType, etc.) remain stale after switchProfileV2, causing
    // DashboardOrPlaceholder and RequireRole to render for the PREVIOUS role.
    // The refreshToken is unchanged on a profile switch — only the access token rotates.
    useEffect(() => {
        return subscribeToProfileSwitch(detail => {
            setAccessToken(detail.newAccessToken);
        });
    }, []);

    const parseJwt = (token: string | null) => {
        if (!token) return null;
        return decodeJwtPayload(token);
    };

    const parseEnumClaim = (val: unknown): number | null => {
        if (val === null || val === undefined) return null;
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
            const n = parseInt(val, 10);
            if (!isNaN(n)) return n;
            const map: Record<string, number> = {
                'SalonOwner': 2,
                'SalonStaff': 3,
                'Customer': 1,
                'StaffManager': 1,
                'Staff': 2,
                'SuperAdmin': 4
            };
            const normalized = val.trim();
            if (map[normalized] !== undefined) return map[normalized];
        }
        return null;
    };

    const claims = parseJwt(accessToken);
    const userType = claims ? parseEnumClaim(claims['userType']) : null;
    const staffRole = claims ? parseEnumClaim(claims['staffRole']) : null;
    const staffId = claims ? (claims['staffId'] ? parseInt(String(claims['staffId']), 10) : null) : null;
    const businessIdClaim = claims ? (claims['businessId'] ? parseInt(String(claims['businessId']), 10) : null) : null;

    const isSalonOwner = userType === 2;
    const isSalonStaff = userType === 3;
    const isSuperAdmin = userType === 4;
    const isStaffManager = staffRole === 1;
    const canManageStaff = isSalonOwner || isStaffManager;
    const canDeleteStaff = isSalonOwner;

    return (
        <AuthContext.Provider value={{
            accessToken,
            refreshToken,
            setTokens,
            clearTokens,
            isAuthenticated: !!accessToken,
            userType,
            staffRole,
            staffId,
            businessId: businessIdClaim,
            isSalonOwner,
            isSalonStaff,
            isSuperAdmin,
            isStaffManager,
            canManageStaff,
            canDeleteStaff,
            emailVerificationRequired,
            setEmailVerificationRequired,
            markEmailVerified,
            pendingVerificationEmail,
            setPendingVerificationEmail,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuthContext must be used within AuthProvider');
    return context;
};
